import React, { useEffect, useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Tabs, TabList, TabPanels, Tab, TabPanel, Button, FormControl, FormLabel, Input, Select,
    Flex, Box, Text, SimpleGrid, IconButton, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Badge, Image, Spinner, useColorModeValue, useToast, Tooltip, Divider, InputGroup, InputLeftElement,
    Stack, Alert, AlertIcon, HStack, Card, CardBody, Collapse, useDisclosure
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiEdit3, FiUser, FiPhone, FiMail, FiMapPin, FiTrash2, FiPlus, FiSave, FiX, FiCheck } from 'react-icons/fi';
import { BsCart4, BsBoxSeam } from 'react-icons/bs';
import { IoMdPricetags } from 'react-icons/io';
import { AsyncPaginate } from 'react-select-async-paginate';
import ReactSelect from 'react-select';
import axiosClient from '../axios-client';
import countriesData from '../assets/dataJson/countries.json';
import { formatPriceForBackend } from '../utils/priceFormatter';
import { useStateContext } from '../context/ContextProvider';

function QuickEditModal({ isOpen, onClose, orderId, onSuccess,
    statusField = 'status_confirmation',
    statusOptions = [
        { value: 'Nouveau', label: 'Nouveau' },
        { value: 'Pas de réponse', label: 'Pas de réponse' },
        { value: 'Messagerie vocale', label: 'Messagerie vocale' },
        { value: 'En attente', label: 'En attente' },
        { value: 'Ligne occupée', label: 'Ligne occupée' },
        { value: 'Mauvais numéro', label: 'Mauvais numéro' },
        { value: 'Annulé', label: 'Annulé' },
        { value: 'Confirmé', label: 'Confirmé' }
    ]
}) {
    const { t } = useTranslation();
    const toast = useToast();
    const { user, currentBusiness } = useStateContext();

    // Theme Colors
    const bgContent = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('gray.50', 'whiteAlpha.100');
    const cardBg = useColorModeValue('white', 'gray.700');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    const badgeBg = useColorModeValue('blue.50', 'blue.900');
    const badgeColor = useColorModeValue('blue.600', 'blue.200');

    // Loading & Order State
    const [loading, setLoading] = useState(false);
    const [detailOrder, setDetailOrder] = useState(null);
    const [productsOrderData, setProductsOrderData] = useState([]);

    // Customer Form State
    const [nomCompletClient, setNomCompletClient] = useState('');
    const [emailClient, setEmailClient] = useState('');
    const [paysClient, setPaysClient] = useState('');
    const [codeCountryClient, setCodeCountryClient] = useState('');
    const [villeClient, setVilleClient] = useState('');
    const [adresseClient, setAdresseClient] = useState('');
    const [regionClient, setRegionClient] = useState('');
    const [codePostalClient, setCodePostalClient] = useState('');
    const [phoneClient, setPhoneClient] = useState('');
    const [phoneCode, setPhoneCode] = useState('+212');
    const [codeCommande, setCodeCommande] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [savingCustomer, setSavingCustomer] = useState(false);

    // Product Add State
    const { isOpen: isAddOpen, onToggle: onToggleAdd, onClose: onCloseAdd } = useDisclosure();
    const [productSelected, setProductSelected] = useState(null);
    const [dataProducts, setDataProducts] = useState([]);
    const [colorSelected, setColorSelected] = useState([]);
    const [sizeSelected, setSizeSelected] = useState([]);
    const [colorInput, setColorInput] = useState(null);
    const [sizeInput, setSizeInput] = useState(null);
    const [quantityInput, setQuantityInput] = useState(1);
    const [purchasePriceInput, setPurchasePriceInput] = useState('');
    const [addingProduct, setAddingProduct] = useState(false);

    // Inline Product Edit State
    const [editingProductId, setEditingProductId] = useState(null);
    const [editColor, setEditColor] = useState('');
    const [editSize, setEditSize] = useState('');
    const [editQuantity, setEditQuantity] = useState('');
    const [editPrice, setEditPrice] = useState('');
    const [savingProduct, setSavingProduct] = useState(false);
    const [deletingProductId, setDeletingProductId] = useState(null);

    const countryOptions = countriesData.countries || [];

    // Fetch Order Details
    const fetchOrderData = async () => {
        if (!orderId || !isOpen) return;
        setLoading(true);
        try {
            const [orderRes, prodRes] = await Promise.all([
                axiosClient.get(`commandes/${orderId}/`),
                axiosClient.get(`/produitCommande/getProductsOrder/?commande=${orderId}&client_id=${user?.id}&userRole=${user?.role}`)
            ]);

            const order = orderRes.data;
            setDetailOrder(order);
            setProductsOrderData(prodRes.data || []);

            // Populate Customer Form
            setCodeCommande(order?.code || '');
            setNomCompletClient(order?.full_name || '');
            setEmailClient(order?.email || '');
            setPaysClient(order?.pays || '');
            setCodeCountryClient(order?.code_country || '');
            setVilleClient(order?.ville || '');
            setAdresseClient(order?.address || '');
            setRegionClient(order?.region || order?.state || '');
            setCodePostalClient(order?.code_postal || order?.postal_code || '');
            setStatusValue(order?.[statusField] || '');

            // Phone Code Parsing
            let initialPhoneCode = '+212';
            if (order?.code_country) {
                const matchedCountry = countryOptions.find(c => c.value === order.code_country);
                if (matchedCountry && matchedCountry.phoneCode) {
                    initialPhoneCode = matchedCountry.phoneCode;
                }
            }

            if (order?.tel) {
                let fullTel = order.tel.trim();
                const sortedCountries = [...countryOptions].sort((a, b) => (b.phoneCode || '').length - (a.phoneCode || '').length);
                const matchingCountry = sortedCountries.find(c => c.phoneCode && fullTel.replace(/\s+/g, '').startsWith(c.phoneCode));

                if (matchingCountry) {
                    setPhoneCode(matchingCountry.phoneCode);
                    let localPhone = fullTel.replace(/\s+/g, '').substring(matchingCountry.phoneCode.length);
                    if (localPhone.startsWith('0')) localPhone = localPhone.substring(1);
                    setPhoneClient(localPhone);
                } else {
                    setPhoneCode(initialPhoneCode);
                    let localPhone = fullTel;
                    if (localPhone.startsWith('0')) localPhone = localPhone.substring(1);
                    setPhoneClient(localPhone);
                }
            } else {
                setPhoneCode(initialPhoneCode);
                setPhoneClient('');
            }
            return order;
        } catch (error) {
            console.error('Error fetching order details for Quick Edit:', error);
            toast({
                description: t("Erreur lors du chargement des détails de la commande"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && orderId) {
            fetchOrderData();
            onCloseAdd();
            setEditingProductId(null);
        }
    }, [isOpen, orderId]);

    // Handle Country Change
    const handleCountryChange = (e) => {
        const selectedCode = e.target.value;
        setCodeCountryClient(selectedCode);
        const countryObj = countryOptions.find(c => c.value === selectedCode);
        if (countryObj) {
            setPaysClient(countryObj.label);
            if (countryObj.phoneCode) {
                setPhoneCode(countryObj.phoneCode);
            }
        }
    };

    // Save Customer Info
    const handleSaveCustomer = async () => {
        if (!nomCompletClient || !phoneClient) {
            toast({
                description: t("Le nom complet et le numéro de téléphone sont obligatoires."),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
            return;
        }

        setSavingCustomer(true);
        try {
            const formData = new FormData();
            formData.append('full_name', nomCompletClient);
            formData.append('code', codeCommande || '');
            if (emailClient) formData.append('email', emailClient);
            if (paysClient) formData.append('pays', paysClient);
            if (codeCountryClient) formData.append('code_country', codeCountryClient);
            if (villeClient) formData.append('ville', villeClient);
            if (adresseClient) formData.append('address', adresseClient);
            if (regionClient) formData.append('region', regionClient);
            if (codePostalClient) formData.append('code_postal', codePostalClient);
            if (statusValue) formData.append(statusField, statusValue);
            formData.append('tel', `${phoneCode ? phoneCode.replace(/\s+/g, '') : ''}${phoneClient ? phoneClient.replace(/\s+/g, '') : ''}`);

            await axiosClient.patch(`commandes/${orderId}/`, formData);
            toast({
                description: t("Informations du client modifiées avec succès"),
                status: 'success',
                duration: 2500,
                isClosable: true,
                position: 'bottom-right'
            });
            const updatedOrder = await fetchOrderData();
            if (onSuccess && updatedOrder) onSuccess(updatedOrder);
        } catch (error) {
            console.error('Error updating customer info:', error);
            let errMsg = t("Une erreur est survenue lors de la modification.");
            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data === 'string') errMsg = data;
                else if (data.message) errMsg = data.message;
                else if (data.error) errMsg = data.error;
            }
            toast({
                description: errMsg,
                status: 'error',
                duration: 3500,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setSavingCustomer(false);
        }
    };

    // Product Search Option Loader
    const loadProductOptions = async (search, loadedOptions, { page }) => {
        try {
            const res = await axiosClient.get(`/products/get_All_Products_names/?client=${user?.id}&page_size=20&page=${page}&search_term=${search}&userRole=${user?.role}&business=${currentBusiness?.id}`, { hideLoader: true });
            const results = res.data.results || [];
            const hasMore = res.data.next !== null && res.data.next !== undefined;
            return {
                options: results.map(product => ({
                    value: product.id,
                    label: product.title
                })),
                hasMore: hasMore,
                additional: { page: page + 1 }
            };
        } catch (error) {
            return { options: [], hasMore: false };
        }
    };

    // When product is selected for adding
    useEffect(() => {
        if (productSelected) {
            axiosClient.get(`products/${productSelected.value}/?business=${currentBusiness?.id}`).then(res => {
                setDataProducts(res.data);
                let cOpts = [];
                let sOpts = [];
                const stockList = res.data?.stock || [];
                for (let i = 0; i < stockList.length; i++) {
                    const color = stockList[i].color;
                    const size = stockList[i].size;
                    if (color && color.length !== 0) {
                        cOpts.push({ value: stockList[i].id, label: color.toLowerCase() });
                    }
                    if (size && size.length !== 0) {
                        sOpts.push({ value: stockList[i].id, label: size.toLowerCase() });
                    }
                }
                cOpts = Array.from(new Map(cOpts.map(item => [item.label.toLowerCase(), item])).values());
                sOpts = Array.from(new Map(sOpts.map(item => [item.label.toLowerCase(), item])).values());
                setColorSelected(cOpts);
                setSizeSelected(sOpts);
                if (res.data?.price) {
                    setPurchasePriceInput(res.data.price);
                }
            }).catch(err => console.error(err));
        }
    }, [productSelected, currentBusiness?.id]);

    const isNumericValid = (value) => /^\d+$/.test(value);
    const isValidPrice = (value) => /^\d*\.?\d{0,2}$/.test(value.toString().replace(',', '.'));

    // Handle Add Product
    const handleAddProduct = async () => {
        if (!productSelected || !quantityInput || !purchasePriceInput) {
            toast({
                description: t("Veuillez remplir tous les champs obligatoires"),
                status: 'error',
                duration: 2500,
                isClosable: true,
                position: 'bottom-right'
            });
            return;
        }

        if (!isNumericValid(quantityInput) || !isValidPrice(purchasePriceInput)) {
            toast({
                description: t("Le champ quantité doit contenir uniquement des chiffres et le prix un montant valide"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
            return;
        }

        setAddingProduct(true);
        try {
            const payload = {
                product: productSelected.value,
                idCommande: orderId,
                color: colorInput ? colorInput.label : "",
                size: sizeInput ? sizeInput.label : "",
                quantity: parseInt(quantityInput),
                price: formatPriceForBackend(purchasePriceInput)
            };
            const res = await axiosClient.post(`produitCommande/createProduitCommande/`, payload);
            setProductsOrderData(res.data || []);
            toast({
                description: t("Produit ajouté avec succès"),
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'bottom-right'
            });
            setProductSelected(null);
            setColorInput(null);
            setSizeInput(null);
            setQuantityInput(1);
            setPurchasePriceInput('');
            onCloseAdd();
            const updatedOrder = await fetchOrderData();
            if (onSuccess && updatedOrder) onSuccess(updatedOrder);
        } catch (error) {
            console.error('Error adding product:', error);
            toast({
                description: t("Erreur lors de l'ajout du produit"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setAddingProduct(false);
        }
    };

    // Start Inline Edit
    const startEditProduct = (prod) => {
        setEditingProductId(prod.id);
        setEditColor(prod.color || '');
        setEditSize(prod.size || '');
        setEditQuantity(prod.quantity || 1);
        setEditPrice(prod.price || 0);
    };

    // Save Inline Edit
    const handleUpdateProduct = async (prodId) => {
        if (!isNumericValid(editQuantity) || !isValidPrice(editPrice)) {
            toast({
                description: t("Quantité ou prix invalide"),
                status: 'error',
                duration: 2500,
                isClosable: true,
                position: 'bottom-right'
            });
            return;
        }

        setSavingProduct(true);
        try {
            const payload = {
                idCommande: orderId,
                color: editColor,
                size: editSize,
                quantity: parseInt(editQuantity),
                price: formatPriceForBackend(editPrice)
            };
            const res = await axiosClient.patch(`produitCommande/updateProduitCommande/?idProductCommande=${prodId}`, payload);
            setProductsOrderData(res.data || []);
            toast({
                description: t("Produit modifié avec succès"),
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'bottom-right'
            });
            setEditingProductId(null);
            const updatedOrder = await fetchOrderData();
            if (onSuccess && updatedOrder) onSuccess(updatedOrder);
        } catch (error) {
            console.error('Error updating product:', error);
            toast({
                description: t("Erreur lors de la modification du produit"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setSavingProduct(false);
        }
    };

    // Handle Delete Product
    const handleDeleteProduct = async (prodId) => {
        setDeletingProductId(prodId);
        try {
            const res = await axiosClient.delete(`produitCommande/deleteProduitCommande/?idProductCommande=${prodId}`);
            setProductsOrderData(res.data || []);
            toast({
                description: t("Produit supprimé avec succès"),
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'bottom-right'
            });
            const updatedOrder = await fetchOrderData();
            if (onSuccess && updatedOrder) onSuccess(updatedOrder);
        } catch (error) {
            console.error('Error deleting product:', error);
            toast({
                description: t("Erreur lors de la suppression du produit"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setDeletingProductId(null);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
            <ModalContent bg={bgContent} borderColor={borderColor} borderWidth={1} rounded="2xl" shadow="2xl" overflow="hidden">
                <ModalHeader bg={headerBg} py={4} px={6} borderBottomWidth={1} borderColor={borderColor}>
                    <Flex align="center" justify="space-between" pr={6}>
                        <HStack spacing={3}>
                            <Flex align="center" justify="center" w={10} h={10} rounded="xl" bg={badgeBg} color={badgeColor} shadow="sm">
                                <FiEdit3 size={20} />
                            </Flex>
                            <Box>
                                <Text fontSize="lg" fontWeight="800" letterSpacing="tight">
                                    {t("Modification rapide de la commande")}
                                </Text>
                                <Text fontSize="xs" color={mutedColor} fontWeight="medium">
                                    {detailOrder?.code ? `${t("Commande")} #${detailOrder.code}` : t("Éditer sans quitter la page")}
                                </Text>
                            </Box>
                        </HStack>
                        {(statusValue || detailOrder?.[statusField]) && (
                            <Badge colorScheme={['Confirmé'].includes(statusValue || detailOrder[statusField]) ? 'green' : ['Pas de réponse', 'Messagerie vocale', 'En attente', 'Ligne occupée'].includes(statusValue || detailOrder[statusField]) ? 'yellow' : ['Mauvais numéro', 'Annulé'].includes(statusValue || detailOrder[statusField]) ? 'red' : 'blue'} px={3} py={1} rounded="full" fontSize="xs">
                                {t(statusValue || detailOrder[statusField])}
                            </Badge>
                        )}
                    </Flex>
                </ModalHeader>
                <ModalCloseButton top={4} right={4} rounded="full" />

                <ModalBody p={6}>
                    {loading ? (
                        <Flex justify="center" align="center" py={16} direction="column" gap={3}>
                            <Spinner size="xl" color="blue.500" thickness="4px" />
                            <Text fontSize="sm" color={mutedColor} fontWeight="medium">{t("Chargement des détails...")}</Text>
                        </Flex>
                    ) : (
                        <Tabs variant="enclosed" colorScheme="blue" isFitted rounded="xl">
                            <TabList mb={6} borderColor={borderColor}>
                                <Tab fontWeight="700" roundedTop="xl" _selected={{ color: 'blue.500', borderColor: borderColor, borderBottomColor: bgContent, bg: bgContent }}>
                                    <HStack spacing={2}>
                                        <FiUser />
                                        <Text>{t("Informations client")}</Text>
                                    </HStack>
                                </Tab>
                                <Tab fontWeight="700" roundedTop="xl" _selected={{ color: 'blue.500', borderColor: borderColor, borderBottomColor: bgContent, bg: bgContent }}>
                                    <HStack spacing={2}>
                                        <BsBoxSeam />
                                        <Text>{t("Produits")}</Text>
                                        <Badge ml={1} colorScheme="blue" rounded="full" px={2}>{productsOrderData.length}</Badge>
                                    </HStack>
                                </Tab>
                            </TabList>

                            <TabPanels>
                                {/* TAB 1: CUSTOMER INFO */}
                                <TabPanel p={0}>
                                    <Stack spacing={5}>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                                            <FormControl isRequired>
                                                <FormLabel fontWeight="600" fontSize="sm" display="flex" alignItems="center" gap={1.5}>
                                                    <FiUser /> {t("Nom complet")}
                                                </FormLabel>
                                                <Input
                                                    placeholder={t("Nom complet")}
                                                    value={nomCompletClient}
                                                    onChange={(e) => setNomCompletClient(e.target.value)}
                                                    rounded="lg"
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontWeight="600" fontSize="sm" display="flex" alignItems="center" gap={1.5}>
                                                    <FiCheck /> {t("Statut de la commande")}
                                                </FormLabel>
                                                <Select
                                                    value={statusValue}
                                                    onChange={(e) => setStatusValue(e.target.value)}
                                                    rounded="lg"
                                                >
                                                    {statusOptions.map((opt, i) => (
                                                        <option key={i} value={opt.value}>{t(opt.label)}</option>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl isRequired>
                                                <FormLabel fontWeight="600" fontSize="sm" display="flex" alignItems="center" gap={1.5}>
                                                    <FiPhone /> {t("Téléphone")}
                                                </FormLabel>
                                                <Flex gap={2}>
                                                    <Select
                                                        w="130px"
                                                        value={codeCountryClient || "MA"}
                                                        onChange={handleCountryChange}
                                                        rounded="lg"
                                                    >
                                                        {countryOptions.map((c, i) => (
                                                            <option key={i} value={c.value}>
                                                                {c.value} ({c.phoneCode})
                                                            </option>
                                                        ))}
                                                    </Select>
                                                    <Input
                                                        flex={1}
                                                        placeholder={t("Numéro de téléphone")}
                                                        value={phoneClient}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/\s+/g, '');
                                                            if (val === '' || /^[0-9]*$/.test(val)) {
                                                                setPhoneClient(val);
                                                            }
                                                        }}
                                                        rounded="lg"
                                                    />
                                                </Flex>
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontWeight="600" fontSize="sm" display="flex" alignItems="center" gap={1.5}>
                                                    <FiMail /> {t("Email (optionnel)")}
                                                </FormLabel>
                                                <Input
                                                    type="email"
                                                    placeholder={t("exemple@domain.com")}
                                                    value={emailClient}
                                                    onChange={(e) => setEmailClient(e.target.value)}
                                                    rounded="lg"
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontWeight="600" fontSize="sm" display="flex" alignItems="center" gap={1.5}>
                                                    <FiMapPin /> {t("Pays")}
                                                </FormLabel>
                                                <ReactSelect
                                                    placeholder={t("Sélectionner un pays")}
                                                    value={paysClient ? { label: paysClient, value: paysClient } : null}
                                                    onChange={(selectedOption) => {
                                                        const label = selectedOption ? selectedOption.label : '';
                                                        setPaysClient(label);
                                                        const countryObj = countryOptions.find(c => c.label === label);
                                                        if (countryObj) {
                                                            setCodeCountryClient(countryObj.value);
                                                            if (countryObj.phoneCode) setPhoneCode(countryObj.phoneCode);
                                                        }
                                                    }}
                                                    options={countryOptions.map(c => ({ label: c.label, value: c.label }))}
                                                    isClearable
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            borderRadius: '0.5rem',
                                                            borderColor: borderColor,
                                                            backgroundColor: 'transparent'
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 50,
                                                            backgroundColor: bgContent
                                                        }),
                                                        option: (base, state) => ({
                                                            ...base,
                                                            backgroundColor: state.isFocused ? (bgContent === 'white' ? '#edf2f7' : '#2d3748') : 'transparent',
                                                            color: state.isFocused ? (bgContent === 'white' ? 'black' : 'white') : 'inherit'
                                                        })
                                                    }}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontWeight="600" fontSize="sm">{t("Ville")}</FormLabel>
                                                <Input
                                                    placeholder={t("Ville")}
                                                    value={villeClient}
                                                    onChange={(e) => setVilleClient(e.target.value)}
                                                    rounded="lg"
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontWeight="600" fontSize="sm">{t("Code de la commande")}</FormLabel>
                                                <Input
                                                    placeholder={t("Code de la commande")}
                                                    value={codeCommande}
                                                    onChange={(e) => setCodeCommande(e.target.value)}
                                                    rounded="lg"
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontWeight="600" fontSize="sm">{t("Adresse complète")}</FormLabel>
                                                <Input
                                                    placeholder={t("Adresse complète")}
                                                    value={adresseClient}
                                                    onChange={(e) => setAdresseClient(e.target.value)}
                                                    rounded="lg"
                                                />
                                            </FormControl>
                                        </SimpleGrid>

                                        <Divider />
                                        <Flex justify="flex-end">
                                            <Button
                                                colorScheme="blue"
                                                leftIcon={<FiSave />}
                                                onClick={handleSaveCustomer}
                                                isLoading={savingCustomer}
                                                loadingText={t("Enregistrement...")}
                                                rounded="xl"
                                                px={8}
                                                shadow="md"
                                                _hover={{ shadow: 'lg', transform: 'translateY(-1px)' }}
                                            >
                                                {t("Enregistrer les informations")}
                                            </Button>
                                        </Flex>
                                    </Stack>
                                </TabPanel>

                                {/* TAB 2: PRODUCT INFO */}
                                <TabPanel p={0}>
                                    <Stack spacing={5}>
                                        {/* Products Summary Card */}
                                        <Card bg={headerBg} rounded="xl" borderWidth={1} borderColor={borderColor} shadow="none">
                                            <CardBody py={3} px={4}>
                                                <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                                                    <HStack spacing={3}>
                                                        <Box p={2.5} bg="blue.500" color="white" rounded="lg">
                                                            <BsCart4 size={18} />
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize="xs" color={mutedColor} fontWeight="700" textTransform="uppercase">{t("Total des articles")}</Text>
                                                            <Text fontSize="md" fontWeight="800">{productsOrderData.length} {t("produit(s)")}</Text>
                                                        </Box>
                                                    </HStack>
                                                    <HStack spacing={3}>
                                                        <Box p={2.5} bg="green.500" color="white" rounded="lg">
                                                            <IoMdPricetags size={18} />
                                                        </Box>
                                                        <Box textAlign="right">
                                                            <Text fontSize="xs" color={mutedColor} fontWeight="700" textTransform="uppercase">{t("Montant total")}</Text>
                                                            <Text fontSize="md" fontWeight="800" color="green.500">
                                                                {detailOrder?.price ? parseFloat(detailOrder.price).toFixed(2) : '0.00'}
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                </Flex>
                                            </CardBody>
                                        </Card>

                                        {/* Products List */}
                                        <Stack spacing={3}>
                                            {productsOrderData.map((prod, idx) => {
                                                const isEditingThis = editingProductId === prod.id;
                                                return (
                                                    <Card key={idx} bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} shadow="sm">
                                                        <CardBody p={4}>
                                                            {isEditingThis ? (
                                                                /* Inline Edit Mode */
                                                                <Stack spacing={3}>
                                                                    <Flex justify="space-between" align="center">
                                                                        <Text fontWeight="700" fontSize="sm" color="blue.500">{t("Modifier")}: {prod.produit?.title}</Text>
                                                                        <IconButton size="xs" icon={<FiX />} onClick={() => setEditingProductId(null)} variant="ghost" rounded="full" />
                                                                    </Flex>
                                                                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                                                        <FormControl>
                                                                            <FormLabel fontSize="xs">{t("Couleur")}</FormLabel>
                                                                            <Input size="sm" value={editColor} onChange={(e) => setEditColor(e.target.value)} rounded="md" />
                                                                        </FormControl>
                                                                        <FormControl>
                                                                            <FormLabel fontSize="xs">{t("Taille")}</FormLabel>
                                                                            <Input size="sm" value={editSize} onChange={(e) => setEditSize(e.target.value)} rounded="md" />
                                                                        </FormControl>
                                                                        <FormControl isRequired>
                                                                            <FormLabel fontSize="xs">{t("Quantité")}</FormLabel>
                                                                            <Input size="sm" type="number" min={1} value={editQuantity} onChange={(e) => setEditQuantity(e.target.value)} rounded="md" />
                                                                        </FormControl>
                                                                        <FormControl isRequired>
                                                                            <FormLabel fontSize="xs">{t("Prix unitaire")}</FormLabel>
                                                                            <Input size="sm" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} rounded="md" />
                                                                        </FormControl>
                                                                    </SimpleGrid>
                                                                    <Flex justify="flex-end" gap={2}>
                                                                        <Button size="sm" variant="ghost" onClick={() => setEditingProductId(null)}>{t("Annuler")}</Button>
                                                                        <Button size="sm" colorScheme="green" leftIcon={<FiCheck />} onClick={() => handleUpdateProduct(prod.id)} isLoading={savingProduct}>{t("Valider")}</Button>
                                                                    </Flex>
                                                                </Stack>
                                                            ) : (
                                                                /* Display Mode */
                                                                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                                                                    <Flex align="center" gap={3} flex="1" minW="200px">
                                                                        <Image
                                                                            boxSize="50px"
                                                                            objectFit="cover"
                                                                            src={prod.produit?.images?.[0]?.image || ''}
                                                                            fallbackSrc="https://via.placeholder.com/50"
                                                                            rounded="lg"
                                                                            borderWidth={1}
                                                                            borderColor={borderColor}
                                                                        />
                                                                        <Box>
                                                                            <Text fontWeight="700" fontSize="sm" noOfLines={1}>{prod.produit?.title || t("Produit indisponible")}</Text>
                                                                            <HStack spacing={2} mt={1}>
                                                                                {prod.color && <Badge colorScheme="purple" fontSize="10px" rounded="full" px={2}>{prod.color}</Badge>}
                                                                                {prod.size && <Badge colorScheme="teal" fontSize="10px" rounded="full" px={2}>{prod.size}</Badge>}
                                                                                {prod.produit?.sku && <Badge colorScheme="gray" fontSize="10px" rounded="full" px={2}>SKU: {prod.produit.sku}</Badge>}
                                                                            </HStack>
                                                                        </Box>
                                                                    </Flex>

                                                                    <HStack spacing={6} align="center">
                                                                        <Box textAlign="center">
                                                                            <Text fontSize="10px" color={mutedColor} fontWeight="700">{t("QTE")}</Text>
                                                                            <Text fontWeight="800" fontSize="md">{prod.quantity}</Text>
                                                                        </Box>
                                                                        <Box textAlign="right" minW="70px">
                                                                            <Text fontSize="10px" color={mutedColor} fontWeight="700">{t("PRIX")}</Text>
                                                                            <Text fontWeight="800" fontSize="md" color="blue.500">{parseFloat(prod.price || 0).toFixed(2)}</Text>
                                                                        </Box>
                                                                        <HStack spacing={1}>
                                                                            <Tooltip label={t("Modifier le produit")}>
                                                                                <IconButton size="sm" icon={<FiEdit3 />} onClick={() => startEditProduct(prod)} variant="ghost" colorScheme="blue" rounded="lg" />
                                                                            </Tooltip>
                                                                            {productsOrderData.length > 1 && (
                                                                                <Tooltip label={t("Supprimer")}>
                                                                                    <IconButton
                                                                                        size="sm"
                                                                                        icon={<FiTrash2 />}
                                                                                        onClick={() => handleDeleteProduct(prod.id)}
                                                                                        isLoading={deletingProductId === prod.id}
                                                                                        variant="ghost"
                                                                                        colorScheme="red"
                                                                                        rounded="lg"
                                                                                    />
                                                                                </Tooltip>
                                                                            )}
                                                                        </HStack>
                                                                    </HStack>
                                                                </Flex>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                );
                                            })}
                                        </Stack>

                                        {/* Add Product Accordion/Section */}
                                        <Box mt={2}>
                                            {!isAddOpen ? (
                                                <Button
                                                    w="full"
                                                    variant="outline"
                                                    colorScheme="blue"
                                                    borderStyle="dashed"
                                                    leftIcon={<FiPlus />}
                                                    onClick={onToggleAdd}
                                                    rounded="xl"
                                                    py={6}
                                                    _hover={{ bg: badgeBg }}
                                                >
                                                    {t("Ajouter un autre produit à cette commande")}
                                                </Button>
                                            ) : (
                                                <Card bg={headerBg} rounded="xl" borderWidth={1} borderColor={borderColor} shadow="md">
                                                    <CardBody p={5}>
                                                        <Flex justify="space-between" align="center" mb={4}>
                                                            <Text fontWeight="700" fontSize="sm" display="flex" alignItems="center" gap={2}>
                                                                <FiPlus color="#3182ce" /> {t("Ajouter un nouveau produit")}
                                                            </Text>
                                                            <IconButton size="xs" icon={<FiX />} onClick={onCloseAdd} variant="ghost" rounded="full" />
                                                        </Flex>
                                                        <Stack spacing={4}>
                                                            <FormControl isRequired>
                                                                <FormLabel fontSize="xs" fontWeight="600">{t("Rechercher un produit")}</FormLabel>
                                                                <AsyncPaginate
                                                                    value={productSelected}
                                                                    loadOptions={loadProductOptions}
                                                                    onChange={setProductSelected}
                                                                    additional={{ page: 1 }}
                                                                    placeholder={t("Rechercher par nom de produit...")}
                                                                />
                                                            </FormControl>

                                                            {productSelected && (
                                                                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                                                                    <FormControl>
                                                                        <FormLabel fontSize="xs">{t("Couleur")}</FormLabel>
                                                                        <Select size="sm" placeholder={t("Sélectionner")} value={colorInput ? colorInput.value : ''} onChange={(e) => {
                                                                            const sel = colorSelected.find(c => c.value === parseInt(e.target.value));
                                                                            setColorInput(sel || null);
                                                                        }} rounded="md">
                                                                            {colorSelected.map((c, idx) => (
                                                                                <option key={idx} value={c.value}>{c.label}</option>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>

                                                                    <FormControl>
                                                                        <FormLabel fontSize="xs">{t("Taille")}</FormLabel>
                                                                        <Select size="sm" placeholder={t("Sélectionner")} value={sizeInput ? sizeInput.value : ''} onChange={(e) => {
                                                                            const sel = sizeSelected.find(s => s.value === parseInt(e.target.value));
                                                                            setSizeInput(sel || null);
                                                                        }} rounded="md">
                                                                            {sizeSelected.map((s, idx) => (
                                                                                <option key={idx} value={s.value}>{s.label}</option>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>

                                                                    <FormControl isRequired>
                                                                        <FormLabel fontSize="xs">{t("Quantité")}</FormLabel>
                                                                        <Input size="sm" type="number" min={1} value={quantityInput} onChange={(e) => setQuantityInput(e.target.value)} rounded="md" />
                                                                    </FormControl>

                                                                    <FormControl isRequired>
                                                                        <FormLabel fontSize="xs">{t("Prix unitaire")}</FormLabel>
                                                                        <Input size="sm" value={purchasePriceInput} onChange={(e) => setPurchasePriceInput(e.target.value)} rounded="md" />
                                                                    </FormControl>
                                                                </SimpleGrid>
                                                            )}

                                                            <Flex justify="flex-end" gap={3} mt={2}>
                                                                <Button size="sm" variant="ghost" onClick={onCloseAdd}>{t("Annuler")}</Button>
                                                                <Button
                                                                    size="sm"
                                                                    colorScheme="blue"
                                                                    leftIcon={<FiPlus />}
                                                                    onClick={handleAddProduct}
                                                                    isLoading={addingProduct}
                                                                    isDisabled={!productSelected}
                                                                    rounded="lg"
                                                                    px={6}
                                                                >
                                                                    {t("Ajouter à la commande")}
                                                                </Button>
                                                            </Flex>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>
                                            )}
                                        </Box>
                                    </Stack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    )}
                </ModalBody>

                <ModalFooter bg={headerBg} py={3} px={6} borderTopWidth={1} borderColor={borderColor}>
                    <Button variant="ghost" onClick={onClose} rounded="xl" fontWeight="600">
                        {t("Fermer")}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default QuickEditModal;
